import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { toast } from 'react-toastify';

export interface IProductStatistics {
  productId: string;
  productName: string;
  price: number;
  orderCount: number;
  subtotal: number;
}

export interface IProductSalesDetailProduct {
  productId: string;
  productName: string;
  price: number;
  orderCount: number;
  pendingCount: number;
  subtotal: number;
}

export interface IProductSalesDetail {
  confirmDt: string;
  totalCount: number;
  totalPendingCount: number;
  products: IProductSalesDetailProduct[];
}

interface ProductSalesDetailPageResponse {
  content: IProductSalesDetail[];
  totalElements: number;
  totalPages: number;
  number: number;
}

type ProductStatisticsData = {
  getProductStatisticsLoading: boolean;
  productStatistics: IProductStatistics[];
  productSalesDetail: IProductSalesDetail[];
  productSalesDetailPage: { totalElements: number; totalPages: number; number: number } | null;
  getProductSalesDetailLoading: boolean;
};

type ProductStatisticsAction = {
  getProductStatistics: (params: {
    startDate: string;
    endDate: string;
  }) => void;
  getProductSalesDetail: (params: {
    startDate: string;
    endDate: string;
    page?: number;
    size?: number;
  }) => void;
  downloadProductStatistics: (params: {
    startDate: string;
    endDate: string;
  }) => Promise<void>;
};

export const useProductStatisticsStore = create<
  ProductStatisticsData & ProductStatisticsAction
>((set) => ({
  getProductStatisticsLoading: false,
  productStatistics: [],
  productSalesDetail: [],
  productSalesDetailPage: null,
  getProductSalesDetailLoading: false,

  getProductStatistics: async ({ startDate, endDate }) => {
    set({
      getProductStatisticsLoading: true,
      productSalesDetail: [],
      productSalesDetailPage: null,
    });

    try {
      const response = await apiClient.get<IProductStatistics[]>(
        '/contract-statistics/product-sales',
        {
          params: { startDate, endDate },
        },
      );

      const data = response.data;
      set({ productStatistics: Array.isArray(data) ? data : [] });
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('상품별 통계를 가져오지 못했습니다.');
    }

    set({ getProductStatisticsLoading: false });
  },

  getProductSalesDetail: async ({ startDate, endDate, page = 0, size = 20 }) => {
    set({ getProductSalesDetailLoading: true });

    try {
      const response = await apiClient.get<ProductSalesDetailPageResponse>(
        '/contract-statistics/product-sales/detail',
        {
          params: { startDate, endDate, page, size },
        },
      );

      set({
        productSalesDetail: response.data.content || [],
        productSalesDetailPage: {
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          number: response.data.number,
        },
      });
    } catch (error) {
      console.error('일자별 상세 통계 API 요청 실패:', error);
      toast.error('일자별 상세 통계를 가져오지 못했습니다.');
    }

    set({ getProductSalesDetailLoading: false });
  },

  downloadProductStatistics: async ({ startDate, endDate }) => {
    try {
      const response = await apiClient.get('/contract-statistics/product-sales/download', {
        params: {
          startDate,
          endDate,
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `상품별_판매통계_${startDate}_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('다운로드가 완료되었습니다.');
    } catch (error) {
      console.error('다운로드 실패:', error);
      toast.error('해당 기간에 일치하는 데이터가 없습니다.');
    }
  },
}));
